import React, { useEffect, useRef, useState } from "react";
import {
    TableOutlined,
    BorderOuterOutlined,
    BorderTopOutlined,
    BorderBottomOutlined,
    BorderLeftOutlined,
    BorderRightOutlined,
    PlusOutlined,
    MinusOutlined,
    DeleteOutlined,
    MoreOutlined,
} from "@ant-design/icons";
import styles from "../styles/QurealRichEditor.module.css";

const TableControls = ({ editor, isOpen, setIsOpen }) => {
    const [showTableTools, setShowTableTools] = useState(false);
    const toolbarRef = useRef(null);
    const menuButtonRef = useRef(null);
    const controlsRef = useRef(null);

    // Check if selection is in a table
    const isInTable = editor && editor.isActive && editor.isActive("table");

    // Show the menu automatically when table is selected
    useEffect(() => {
        if (isInTable) {
            // Add a small delay to ensure DOM is ready
            const timer = setTimeout(() => {
                // Force position the menu button to appear at the table
                const tableElement = document.querySelector(".tiptap-table");
                if (tableElement && controlsRef.current) {
                    // Make sure the table controls are visible and positioned correctly
                    controlsRef.current.style.position = "absolute";
                    controlsRef.current.style.display = "block";
                    controlsRef.current.style.zIndex = "1000";

                    // Position relative to the table
                    const tableRect = tableElement.getBoundingClientRect();
                    const editorElement =
                        document.querySelector(".ProseMirror");
                    const editorRect = editorElement
                        ? editorElement.getBoundingClientRect()
                        : null;

                    if (editorRect) {
                        controlsRef.current.style.top = `${
                            tableRect.top - editorRect.top - 10
                        }px`;
                        controlsRef.current.style.left = `${
                            tableRect.left - editorRect.left - 10
                        }px`;
                    }
                }
            }, 100);

            return () => clearTimeout(timer);
        }
    }, [isInTable]);

    // Close toolbar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                toolbarRef.current &&
                !toolbarRef.current.contains(event.target) &&
                menuButtonRef.current &&
                !menuButtonRef.current.contains(event.target)
            ) {
                setShowTableTools(false);
            }
        };

        if (showTableTools) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [showTableTools]);

    // Check table capabilities for enabling/disabling buttons
    const capabilities = {
        addColumnBefore: editor && editor.can && editor.can().addColumnBefore(),
        addColumnAfter: editor && editor.can && editor.can().addColumnAfter(),
        deleteColumn: editor && editor.can && editor.can().deleteColumn(),
        addRowBefore: editor && editor.can && editor.can().addRowBefore(),
        addRowAfter: editor && editor.can && editor.can().addRowAfter(),
        deleteRow: editor && editor.can && editor.can().deleteRow(),
        toggleHeaderCell:
            editor && editor.can && editor.can().toggleHeaderCell(),
        mergeOrSplit: editor && editor.can && editor.can().mergeOrSplit(),
        deleteTable: editor && editor.can && editor.can().deleteTable(),
        toggleHeaderRow:
            editor && editor.can && editor.can().toggleHeaderRow?.(),
        toggleHeaderColumn:
            editor && editor.can && editor.can().toggleHeaderColumn?.(),
    };

    // Render the initial table insertion button if not in a table
    if (!isInTable) {
        return (
            <button
                onClick={() => {
                    editor
                        .chain()
                        .focus()
                        .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                        .run();
                }}
                title="Insert Table"
                className={styles.button}
            >
                <TableOutlined />
            </button>
        );
    }

    // Render the three-dot menu button when a table is active
    return (
        <div className={styles.modernTableControls} ref={controlsRef}>
            <button
                ref={menuButtonRef}
                onClick={() => setShowTableTools(!showTableTools)}
                title="Table Options"
                className={`${styles.tableMenuButton} ${
                    showTableTools ? styles.active : ""
                }`}
            >
                <MoreOutlined />
            </button>

            {showTableTools && (
                <div ref={toolbarRef} className={styles.tableToolbar}>
                    <button
                        onClick={() =>
                            editor.chain().focus().addColumnBefore().run()
                        }
                        disabled={!capabilities.addColumnBefore}
                        title="Add Column Before"
                        className={styles.toolbarButton}
                    >
                        <BorderLeftOutlined />
                    </button>

                    <button
                        onClick={() =>
                            editor.chain().focus().addColumnAfter().run()
                        }
                        disabled={!capabilities.addColumnAfter}
                        title="Add Column After"
                        className={styles.toolbarButton}
                    >
                        <BorderRightOutlined />
                    </button>

                    <button
                        onClick={() =>
                            editor.chain().focus().deleteColumn().run()
                        }
                        disabled={!capabilities.deleteColumn}
                        title="Delete Column"
                        className={styles.toolbarButton}
                    >
                        <MinusOutlined />
                    </button>

                    <div className={styles.toolbarDivider}></div>

                    <button
                        onClick={() =>
                            editor.chain().focus().addRowBefore().run()
                        }
                        disabled={!capabilities.addRowBefore}
                        title="Add Row Before"
                        className={styles.toolbarButton}
                    >
                        <BorderTopOutlined />
                    </button>

                    <button
                        onClick={() =>
                            editor.chain().focus().addRowAfter().run()
                        }
                        disabled={!capabilities.addRowAfter}
                        title="Add Row After"
                        className={styles.toolbarButton}
                    >
                        <BorderBottomOutlined />
                    </button>

                    <button
                        onClick={() => editor.chain().focus().deleteRow().run()}
                        disabled={!capabilities.deleteRow}
                        title="Delete Row"
                        className={styles.toolbarButton}
                    >
                        <MinusOutlined />
                    </button>

                    <div className={styles.toolbarDivider}></div>

                    {capabilities.toggleHeaderRow && (
                        <button
                            onClick={() =>
                                editor.chain().focus().toggleHeaderRow().run()
                            }
                            disabled={!capabilities.toggleHeaderRow}
                            title="Toggle Header Row"
                            className={styles.toolbarButton}
                        >
                            <BorderTopOutlined />
                        </button>
                    )}

                    {capabilities.toggleHeaderColumn && (
                        <button
                            onClick={() =>
                                editor
                                    .chain()
                                    .focus()
                                    .toggleHeaderColumn()
                                    .run()
                            }
                            disabled={!capabilities.toggleHeaderColumn}
                            title="Toggle Header Column"
                            className={styles.toolbarButton}
                        >
                            <BorderLeftOutlined />
                        </button>
                    )}

                    <button
                        onClick={() =>
                            editor.chain().focus().toggleHeaderCell().run()
                        }
                        disabled={!capabilities.toggleHeaderCell}
                        title="Toggle Header Cell"
                        className={`${styles.toolbarButton} ${
                            editor.isActive("tableHeader") ? styles.active : ""
                        }`}
                    >
                        <BorderOuterOutlined />
                    </button>

                    <button
                        onClick={() =>
                            editor.chain().focus().mergeOrSplit().run()
                        }
                        disabled={!capabilities.mergeOrSplit}
                        title="Merge or Split Cells"
                        className={styles.toolbarButton}
                    >
                        <PlusOutlined />
                    </button>

                    <div className={styles.toolbarDivider}></div>

                    <button
                        onClick={() =>
                            editor.chain().focus().deleteTable().run()
                        }
                        disabled={!capabilities.deleteTable}
                        title="Delete Table"
                        className={styles.toolbarButton}
                    >
                        <DeleteOutlined />
                    </button>
                </div>
            )}
        </div>
    );
};

export default TableControls;
